import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import ChatMarkdown from './ChatMarkdown';
import icon from '../Asset/www.png';
import './AIChatBubble.css';

import { getAttribution } from '../lib/attribution';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'https://dash.softup.agency';

// ── Call backend proxy (plain JSON, Lightning AI doesn't support SSE) ──────────
// A stable id for this conversation, so a second capture in the same chat
// updates the lead rather than creating a duplicate person. sessionStorage,
// not localStorage: a new visit is genuinely a new conversation.
function conversationId() {
  try {
    let id = window.sessionStorage.getItem('su_conversation');
    if (!id) {
      id = 'c-' + Date.now().toString(36) + '-' + Math.random().toString(36).slice(2, 8);
      window.sessionStorage.setItem('su_conversation', id);
    }
    return id;
  } catch (err) {
    return undefined; // private mode: capture still works, dedupe does not
  }
}

async function callLightningAI(history, userText, signal, onChunk) {
  const messages = [
    ...history.map(m => ({ role: m.role, content: m.content })),
    { role: 'user', content: userText },
  ];

  const resp = await fetch(`${BACKEND_URL}/api/ai-chat`, {
    method: 'POST',
    signal,
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      messages,
      locale: typeof document !== 'undefined' ? document.documentElement.lang : undefined,
      conversation_id: conversationId(),
      source_page: typeof window !== 'undefined' ? window.location.pathname : undefined,
      // So an enquiry captured in chat is traceable to the ad that paid for it.
      attribution: getAttribution(),
    }),
  });

  if (!resp.ok) throw new Error(`API ${resp.status}`);

  const data = await resp.json();
  if (data.error) throw new Error(data.error);
  const content = data.content || '';
  onChunk(content);
  return content;
}

// ── SVG Icons ──────────────────────────────────────────────────────────────────
const BotIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="11" width="18" height="10" rx="2"/>
    <path d="M9 11V8a3 3 0 0 1 6 0v3"/>
    <circle cx="9" cy="16" r="1" fill="currentColor"/>
    <circle cx="15" cy="16" r="1" fill="currentColor"/>
    <path d="M12 2v2M8 4l1 2M16 4l-1 2"/>
  </svg>
);
const XIcon = () => (
  <svg width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
    <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
  </svg>
);
const SendIcon = () => (
  <svg width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
    <line x1="22" y1="2" x2="11" y2="13"/><polygon points="22 2 15 22 11 13 2 9 22 2"/>
  </svg>
);
const MaxIcon = () => (
  <svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
    <polyline points="15 3 21 3 21 9"/><polyline points="9 21 3 21 3 15"/>
    <line x1="21" y1="3" x2="14" y2="10"/><line x1="3" y1="21" x2="10" y2="14"/>
  </svg>
);


const QUICK_PROMPTS = [
  'What services do you offer?',
  'How quickly can you deliver a project?',
  'How do I book a free consultation?',
];

// ── Main component ─────────────────────────────────────────────────────────────
export default function AIChatBubble() {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const endRef = useRef(null);
  const inputRef = useRef(null);
  const abortRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  useEffect(() => {
    if (open) setTimeout(() => inputRef.current?.focus(), 50);
  }, [open]);

  const send = async (overrideText) => {
    const text = (overrideText ?? input).trim();
    if (!text || loading) return;

    const asstId = `a${Date.now()}`;
    setMessages(prev => [
      ...prev,
      { id: `u${Date.now()}`, role: 'user', content: text },
      { id: asstId, role: 'assistant', content: '' },
    ]);
    setInput('');
    setLoading(true);

    const ctrl = new AbortController();
    abortRef.current = ctrl;

    const historyBeforeSend = messages;

    try {
      await callLightningAI(historyBeforeSend, text, ctrl.signal, (full) => {
        setMessages(prev => prev.map(m => m.id === asstId ? { ...m, content: full } : m));
      });
    } catch (err) {
      if (err.name !== 'AbortError') {
        setMessages(prev => prev.map(m =>
          m.id === asstId
            ? { ...m, content: "Sorry, I couldn't connect to the AI. Please try again or open the full chat." }
            : m
        ));
      }
    } finally {
      setLoading(false);
    }
  };

  const onKey = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); }
  };

  const lastMsgIsAssistant = messages.length > 0 && messages[messages.length - 1].role === 'assistant';
  const showTypingDot = loading && !lastMsgIsAssistant;

  return (
    <div className="aicb-wrapper">
      {open && (
        <div className="aicb-panel">
          {/* ── Header ── */}
          <div className="aicb-panel-header">
            <div className="aicb-header-left">
              <img src={icon} alt="SoftUp" className="aicb-header-avatar" />
              <div className="aicb-header-text">
                <span className="aicb-header-name">SoftUp Agent</span>
                <span className="aicb-header-status">
                  <span className="aicb-online-dot" /> Online
                </span>
              </div>
            </div>
            <div className="aicb-header-right">
              <button className="aicb-hdr-btn" onClick={() => navigate('/ai-chat')} title="Open full chat">
                <MaxIcon />
              </button>
              <button className="aicb-hdr-btn" onClick={() => setOpen(false)} title="Close">
                <XIcon />
              </button>
            </div>
          </div>

          {/* ── Messages ── */}
          <div className="aicb-panel-body">
            {messages.length === 0 && (
              <div className="aicb-empty-state">
                <div className="aicb-empty-avatar">
                  <img src={icon} alt="SoftUp" />
                </div>
                <p className="aicb-empty-greeting">
                  Hi there! I'm SoftUp's AI assistant. Ask me anything about our services, or how we can help build your next product.
                </p>
                <div className="aicb-quick-list">
                  {QUICK_PROMPTS.map(q => (
                    <button key={q} className="aicb-quick-chip" onClick={() => send(q)}>
                      {q}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {messages.map(m => (
              <div key={m.id} className={`aicb-row ${m.role}`}>
                {m.role === 'assistant' && (
                  <img src={icon} alt="Agent" className="aicb-row-avatar" />
                )}
                <div className={`aicb-bubble-msg ${m.role}`}>
                  {m.role === 'assistant'
                    ? <ChatMarkdown content={m.content || '…'} />
                    : <span>{m.content}</span>
                  }
                </div>
              </div>
            ))}

            {showTypingDot && (
              <div className="aicb-row assistant">
                <img src={icon} alt="Agent" className="aicb-row-avatar" />
                <div className="aicb-bubble-msg assistant aicb-typing-dots">
                  <span /><span /><span />
                </div>
              </div>
            )}

            <div ref={endRef} />
          </div>

          {/* ── Input ── */}
          <div className="aicb-panel-footer">
            <textarea
              ref={inputRef}
              className="aicb-panel-input"
              value={input}
              onChange={e => setInput(e.target.value)}
              onKeyDown={onKey}
              placeholder="Message SoftUp Agent…"
              rows={1}
            />
            <button
              className="aicb-panel-send"
              onClick={() => send()}
              disabled={!input.trim() || loading}
            >
              <SendIcon />
            </button>
          </div>
        </div>
      )}

      {/* ── Bubble trigger ── */}
      <button
        className={`aicb-bubble ${open ? 'active' : ''}`}
        onClick={() => setOpen(v => !v)}
        aria-label="Open AI Chat"
      >
        <span className="aicb-pulse" />
        {open ? <XIcon /> : <BotIcon />}
      </button>
    </div>
  );
}
